// The ONE definition of the structural-keyword-typo diagnostic message format.
//
// The witness (`treeSitterSyntaxWitness.findTypoWitness`) BUILDS the message
// here; the `fix-keyword-typo` code action READS it back here. Keeping both
// sides on one module is what makes the quick fix safe: the marker that Monaco
// paints carries only `message` + `code` (Monaco's `IMarkerData` has no
// free-form payload), so the suggested keyword has to survive inside the text —
// and a private regex on the consumer side would silently rot the moment the
// wording changed.

export type KeywordTypo = {
  /** The misspelled token exactly as the user wrote it (`FORM`, `selct`). */
  readonly raw: string;
  /** The structural keyword the witness matched, always UPPERCASE (`FROM`). */
  readonly keyword: string;
};

const PREFIX = 'Possible typo "';
const MIDDLE = '" — did you mean ';
const SUFFIX = '?';

export function buildKeywordTypoMessage(raw: string, keyword: string): string {
  return `${PREFIX}${raw}${MIDDLE}${keyword}${SUFFIX}`;
}

/** Inverse of `buildKeywordTypoMessage`; `null` for any other message (the
 *  structural witnesses, LSP diagnostics, ANTLR messages on mssql). */
export function parseKeywordTypoMessage(message: string): KeywordTypo | null {
  if (!message.startsWith(PREFIX) || !message.endsWith(SUFFIX)) return null;
  const body = message.slice(PREFIX.length, message.length - SUFFIX.length);
  const separator = body.indexOf(MIDDLE);
  if (separator < 0) return null;
  const raw = body.slice(0, separator);
  const keyword = body.slice(separator + MIDDLE.length);
  // The witness only ever emits a word token and an uppercase keyword; anything
  // else means the message was not produced by the witness.
  if (!/^[A-Za-z_][A-Za-z0-9_$]*$/.test(raw)) return null;
  if (!/^[A-Z]+$/.test(keyword)) return null;
  return { raw, keyword };
}
